
import React, { useState, CSSProperties, useCallback } from "react";
import { Campaign, CharacterItem, Quest, Receivable, ReceivableItem } from "@dndModels";
import DandDAPI from "@utilities/smart_api";
import EditButton from "@components/input/buttons/icon_buttons/EditButton";
import FlexBox from "@components/layout/FlexBox";
import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import StringBox from "@components/input/strings/StringBox";

import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import EyeButton from "@components/input/buttons/icon_buttons/EyeButton";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import { CALENDARS, dateString, parseDate } from "@dndModels/Calendars";
import CalendarBox from "@components/input/calendar/CalendarBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import { useSearchParams } from "react-router-dom";
import Star from "@mui/icons-material/Star";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DataUtilities from "@dndUtilities/data_utilities";
import CharConnectUtilities from "@dndUtilities/char_connect_utilities";
import CommonUtilities from "@utilities/common_utilities";
import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";
import QuestRewardsEdit from "./QuestRewardsEdit";

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const data_util = DataUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  campaign: Campaign;
  quest: Quest;
  index: number;
  enabled: boolean;
  onChange: () => void;
}


const styles: Record<string, CSSProperties> = {
  boardFrame: {
    maxWidth: 980,
    margin: "0 auto",
    background:
      "repeating-linear-gradient(90deg, #4a3220 0px, #6b4a2f 3px, #4a3220 6px)",
    border: "14px solid #2b1b12",
    borderRadius: 6,
    boxShadow:
      "0 0 0 4px #6b4a2f inset, 0 25px 60px rgba(0,0,0,0.6), 0 0 40px rgba(0,0,0,0.5) inset",
    padding: "28px 22px 36px",
    position: "relative",
    boxSizing: "border-box",
  },
  innerBorder: {
    position: "absolute",
    inset: 8,
    border: "2px solid rgba(0,0,0,0.35)",
    borderRadius: 4,
    pointerEvents: "none",
  },
  header: {
    textAlign: "center",
    marginBottom: 8,
  },
  crest: {
    width: 64,
    height: 64,
    display: "block",
    margin: "0 auto 6px",
    opacity: 0.9,
  },
  h1: {
    fontFamily: "'Cinzel Decorative', serif",
    fontWeight: 900,
    color: "#ecdfbd",
    fontSize: "clamp(1.6rem, 4vw, 2.6rem)",
    textShadow: "0 2px 0 #000, 0 0 18px rgba(184,137,58,0.5)",
    margin: "0 0 4px",
    letterSpacing: 1,
  },
  subtitle: {
    fontFamily: "'Cinzel', serif",
    color: "#b8893a",
    fontSize: "0.95rem",
    letterSpacing: 3,
    textTransform: "uppercase",
    marginBottom: 18,
    textShadow: "0 1px 0 #000",
  },
  magistrateNote: {
    maxWidth: 640,
    margin: "0 auto 26px",
    textAlign: "center",
    color: "#dccb9e",
    fontStyle: "italic",
    fontSize: "1.02rem",
    lineHeight: 1.5,
    padding: "0 12px",
  },
  tally: {
    textAlign: "center",
    fontFamily: "'Cinzel', serif",
    color: "#ecdfbd",
    fontSize: "0.95rem",
    letterSpacing: 1,
    marginBottom: 24,
  },
  tallyCount: {
    color: "#b8893a",
    fontWeight: 700,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 22,
  },
  pin: {
    position: "absolute",
    top: -10,
    left: "50%",
    transform: "translateX(-50%)",
    width: 16,
    height: 16,
    borderRadius: "50%",
    background:
      "radial-gradient(circle at 35% 30%, #e8c466, #b8893a 60%, #7a541c)",
    boxShadow: "0 3px 4px rgba(0,0,0,0.5)",
    zIndex: 3,
  },
  questTitle: {
    fontFamily: "'Cinzel', serif",
    fontWeight: 700,
    fontSize: "1.08rem",
    color: "#2e2014",
    lineHeight: 1.25,
  },
  questTitleCompleted: {
    textDecoration: "line-through",
    textDecorationColor: "#5e1c1c",
    textDecorationThickness: "2px",
  },
  questBody: {
    fontSize: "0.98rem",
    lineHeight: 1.45,
    marginBottom: 12,
    color: "#3a2a18",
  },
  questMeta: {
    display: "flex",
    borderTop: "1px dashed rgba(46,32,20,0.35)",
    paddingTop: 10,
    fontSize: "0.88rem",
  },
  reward: {
    fontStyle: "italic",
    color: "#5e1c1c",
  },
  rewardBold: {
    fontStyle: "normal",
    fontWeight: 700,
  },
  sealBtn: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    background:
      "radial-gradient(circle at 35% 30%, #b94a4a, #8b2a2a 55%, #5e1c1c 100%)",
    boxShadow:
      "0 3px 6px rgba(0,0,0,0.5), inset 0 0 0 2px rgba(255,255,255,0.15)",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: "rgba(255,255,255,0.85)",
    transition: "transform 0.15s ease",
  },
  claimBtn: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    background:
      "radial-gradient(circle at 35% 30%,rgb(9, 65, 168),rgb(47, 57, 104) 55%,rgb(26, 101, 122) 100%)",
    boxShadow:
      "0 3px 6px rgba(0,0,0,0.5), inset 0 0 0 2px rgba(255,255,255,0.15)",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: "rgba(255,255,255,0.85)",
    transition: "transform 0.15s ease",
  },
  completedStamp: {
    fontFamily: "'Cinzel', serif",
    fontWeight: 700,
    fontSize: "1.6rem",
    color: "#8b2a2a",
    border: "4px solid #8b2a2a",
    padding: "2px 14px",
    borderRadius: 4,
    opacity: 0.55,
    letterSpacing: 2,
    whiteSpace: "nowrap",
    transform: "rotate(-12deg)",
    // Was in stampLayer
    position: "absolute",
    // inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    pointerEvents: "none",
    zIndex: 10,
  },
  hiddenStamp: {
    fontFamily: "'Cinzel', serif",
    fontWeight: 700,
    fontSize: "1.6rem",
    color: "#8b2a2a",
    border: "4px solid #8b2a2a",
    padding: "2px 14px",
    borderRadius: 4,
    opacity: 0.55,
    letterSpacing: 2,
    whiteSpace: "nowrap",
    // transform: "translate(-50%, -50%)",
    transform: `rotate(12deg)`,
    top: "38%",
    left: "50%",
    // Was in stampLayer
    position: "absolute",
    // inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    pointerEvents: "none",
    zIndex: 10,
  },
  claimedStamp: {
    fontFamily: "'Cinzel', serif",
    fontWeight: 700,
    fontSize: "1.6rem",
    color: "#8b2a2a",
    border: "4px solid #8b2a2a",
    padding: "2px 14px",
    borderRadius: 4,
    opacity: 0.55,
    letterSpacing: 2,
    whiteSpace: "nowrap",
    top: 200,
    transform: `rotate(-5deg)`,
    // Was in stampLayer
    position: "absolute",
    // inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    pointerEvents: "none",
    zIndex: 10,
  },
  grandPrizeBase: {
    maxWidth: 520,
    margin: "26px auto 0",
    textAlign: "center",
    background: "linear-gradient(160deg, #ecdfbd, #dccb9e)",
    borderRadius: 4,
    padding: "14px 18px",
    // clipPath: "polygon(1% 8%, 99% 0%, 100% 92%, 0% 100%)",
    transition: "box-shadow 0.4s ease",
  },
  grandPrizeH3: {
    fontFamily: "'Cinzel', serif",
    margin: "0 0 4px",
    color: "#5e1c1c",
    fontSize: "1rem",
    letterSpacing: 1,
  },
  grandPrizeP: {
    margin: 0,
    fontSize: "0.95rem",
    color: "#2e2014",
  },
  footerNote: {
    textAlign: "center",
    marginTop: 30,
    color: "#dccb9e",
    fontFamily: "'Cinzel', serif",
    fontSize: "0.85rem",
    letterSpacing: 1,
  },
};

export default function QuestCard({ campaign, quest, index, enabled, onChange }: Props) {
  const [hovered, setHovered] = useState(false);
  const [claim_hovered, setClaimHovered] = useState(false);
  const [ping, setPing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const quest_param = searchParams.get("quest");
  const [expanded, setExpanded] = useState(quest.true_id === quest_param);
  const [granting, setGranting] = useState(false);

  const handleQuestExpandedChange = useCallback((quest_id: string, open: boolean) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (open) {
        params.set("quest", quest_id);
      } else if (params.get("quest") === quest_id) {
        // Only clear if this quest owns the param — avoids a stale
        // close callback from a previously-open modal wiping a newer one.
        params.delete("quest");
      }
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  const tilt = index % 2 === 0 ? -1.1 : 0.9;

  const questStyle: CSSProperties = {
    transform: enabled || hovered ? "rotate(0deg) translateY(-3px)" : `rotate(${tilt}deg)`,
    boxShadow: hovered
      ? "0 14px 26px rgba(0,0,0,0.5)"
      : "0 8px 18px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.15)",
    filter: quest.completed ? "grayscale(0.35) brightness(0.92)" : "none",
  };

  const claimBtnStyle: CSSProperties = {
    ...styles.sealBtn,
    transform: claim_hovered ? "scale(1.08)" : "scale(1)",
  };

  const renderPosted = () => {
    if (campaign.calendar_id !== "") {
      return (
        <FlexBox direction="row">
          <FlexBox>
            { enabled ? 
              <div className="centered padded" style={{ backgroundColor: "black" }}>
                <CalendarBox 
                  name="Posted Date"
                  value={quest.posted}
                  today={campaign.current_date}
                  calendar_id={campaign.calendar_id}
                  onChange={ async (changed: number) => {
                    quest.posted = changed;
                    onChange();
                  }}
                />
              </div>
            : 
              <span className="small_label gray">Posted Date: {dateString(quest.posted, campaign.calendar_id)}</span>
            }
          </FlexBox>
        </FlexBox>
      );
    }
  }

  const renderExtras = () => {
    return (
      <ModalOrDrawer open={expanded} onClose={() => {
        handleQuestExpandedChange(quest.true_id, false);
        setExpanded(false);
      }}>
        <>
          { theCard(true) }
        </>
      </ModalOrDrawer>
    );  
  }

  const renderStars = () => {
    const return_me: any[] = [];
    for (let i = 0; i < quest.stars; ++i) {
      return_me.push(<Star key={i} />);
    }
    return return_me;
  }

  // const renderRewardDetails = () => {
  //   const money = quest.reward.money.toString().trim();
  //   const items = quest.reward.items.map(reward_item => {
  //     const pseudo_item = char_util.getPseudoItem(reward_item.id);
  //     const name = pseudo_item ? pseudo_item.name : reward_item.id;
  //     return `${reward_item.count > 1 ? `${reward_item.count} ` : ""}${name}${reward_item.for_all ? " (each)" : ""}`;
  //   });
  //   return (
  //     <>
  //       { money !== "" && <div>{ money }</div> }
  //       { items.map((item, key) => <div key={key}>{ item }</div>) }
  //     </>
  //   );
  // }

  // Money and shared items go to the campaign inventory. 
  // Items marked for_all get sent to each active party member as a Receivable.
  const grantRewards = async () => {
    setGranting(true);
    try {
      // Get the latest campaign inventory so we don't overwrite changes from elsewhere.
      await api.loadCampaignInventory({ campaign });
      if (data_util.items.length === 0) {
        await data_util.loadData({ char: campaign });
      }
      await data_util.core_data_loading;
      await connect_util.connectItems(campaign);

      campaign.inventory.money.add(quest.reward.money);

      for (const reward_item of quest.reward.items.filter(o => !o.for_all)) {
        const pseudo_item = char_util.getPseudoItem(reward_item.id);
        if (!pseudo_item || !pseudo_item.base_item) {
          console.error(`Quest reward item not found: ${reward_item.id}`);
          continue;
        }
        // addItem only makes one of an item that doesn't stack, so add those one at a time.
        const probe = await connect_util.createCharItem({ char: campaign, item: pseudo_item });
        if (probe.stackable) {
          await connect_util.addItem({ char: campaign, item: pseudo_item, count: reward_item.count });
        } else {
          for (let i = 0; i < reward_item.count; ++i) {
            await connect_util.addItem({ char: campaign, item: pseudo_item });
          }
        }
      }

      await api.upsertObject("campaign_inventory", campaign.inventory);
      await connect_util.refreshCampaignInventory(campaign, campaign._id);

      const for_all_rewards = quest.reward.items.filter(o => o.for_all);
      if (for_all_rewards.length > 0) {
        await api.loadCChars({ campaign });
        const party_char_ids = campaign.c_chars.filter(o => o.active).map(o => o.char_id);
        for (const char_id of party_char_ids) {
          const receivable = new Receivable();
          receivable.from_id = "DM";
          receivable.char_id = char_id;
          receivable.sent_dt = common_util.get_time_number();
          receivable.message = `Reward for completing ${quest.title}`;
          receivable.origin_id = api.temp_id;
          receivable.type = "Mail";
          // Each party member needs their own copies of the items.
          for (const reward_item of for_all_rewards) {
            const char_items = await makeRewardCharItems(reward_item.id, reward_item.count);
            receivable.items.push(...char_items.map(char_item => {
              const receivable_item = new ReceivableItem();
              receivable_item.type = "Item";
              receivable_item.obj = char_item;
              return receivable_item;
            }));
          }
          await api.upsertObject("receivable", receivable);
        }
      }

      quest.rewards_granted = true;
      campaign.subscription_holder.itemsChanged();
      onChange();
    } finally {
      setGranting(false);
    }
  }

  // Stackable items come back as one stack, others as one CharacterItem each.
  const makeRewardCharItems = async (pseudo_item_id: string, count: number): Promise<CharacterItem[]> => {
    const return_me: CharacterItem[] = [];
    const pseudo_item = char_util.getPseudoItem(pseudo_item_id);
    if (pseudo_item && pseudo_item.base_item) {
      const stack = await connect_util.createCharItem({ char: campaign, item: pseudo_item, count });
      if (stack.stackable) {
        return_me.push(stack);
      } else {
        for (let i = 0; i < count; ++i) {
          return_me.push(await connect_util.createCharItem({ char: campaign, item: pseudo_item, count: 1 }));
        }
      }
    }
    return return_me;
  }

  const renderGrantRewards = () => {
    // if (quest.title.includes("Experiment")) {
    //   debugger;
    // }
    if (enabled || !quest.completed || api.user_id !== campaign.owner_id) {
      return null;
    }
    if (quest.rewards_granted) {
      return (
        <FlexBox className="centered small_label gray">Rewards Granted</FlexBox>
      );
    }
    return (
      <FlexBox className="centered">
        <DandDButtonBox
          name={ granting ? "Granting Rewards..." : "Grant Rewards" }
          disabled={granting}
          onClick={grantRewards}
        />
      </FlexBox>
    );
  }

  const theCard = (opened = false) => {
    return (
      <div className="parchment-wrapper double-padded">
        <div className={ enabled ? "bordered padded" : "parchment-scroll double-padded" }>
          <div style={{ position: "relative" }}>
            <div style={ enabled ? {} : styles.pin } />
            {!enabled && (quest.completed || quest.hidden || quest.claimed_by !== "") && (
              <>
                {quest.completed && <div style={styles.completedStamp}>COMPLETED</div>}
                {quest.hidden && <div style={styles.hiddenStamp}>HIDDEN</div>}
                {quest.claimed_by !== "" && <div style={styles.claimedStamp}>Claimed By: {quest.claimed_by === "The Party" && campaign.party_name !== "" ? campaign.party_name : quest.claimed_by }</div>}
              </>
            )}
            <FlexBox direction={ enabled ? "row" : "column" }
              style={ enabled ? {} : {
                ...styles.questTitle,
                ...(quest.completed ? styles.questTitleCompleted : {}),
              }}
            >
              <FlexBox className="centered">
                { enabled ? 
                  <div style={{ backgroundColor: "black" }}>
                    <StringBox 
                      name="Title"
                      value={quest.title}
                      onBlur={ async (changed: string) => {
                        quest.title = changed;
                        onChange();
                      }}
                    />
                  </div>
                : 
                  <div className="clickable" onClick={() => {
                    handleQuestExpandedChange(quest.true_id, !expanded);
                    setExpanded(!expanded); 
                  }}>
                    <DisplayStringWithReplacements display_string={quest.title} /> 
                  </div>
                }
              </FlexBox>
              <FlexBox className="centered" style={{ padding: "4px" }}>
                { enabled ? 
                  <div style={{ backgroundColor: "black" }}>
                    <SelectStringBox 
                      name="Difficulty"
                      value={quest.difficulty}
                      label_above
                      options={["Easy","Medium","Hard","Deadly"]}
                      onChange={ async (changed: "Easy" | "Medium" | "Hard" | "Deadly") => {
                        quest.difficulty = changed;
                        onChange();
                      }}
                    />
                  </div>
                : 
                  <>Difficulty: { quest.difficulty }</>
                }
              </FlexBox>
              <FlexBox className="centered" style={{ padding: "4px" }}>
                { enabled ? 
                  <div style={{ backgroundColor: "black" }}>
                    <SelectStringBox 
                      name="Stars"
                      value={`${quest.stars}`}
                      options={["1","2","3"]}
                      label_above
                      onChange={ async (changed: string) => {
                        quest.stars = +changed as 1 | 2 | 3;
                        onChange();
                      }}
                    />
                  </div>
                : 
                  <>{ renderStars() }</>
                }
              </FlexBox>
              { enabled &&
                <FlexBox direction="row" style={{ float: "right" }}>
                  <FlexBox xs_expand={0}>
                    <ToggleButtonBox
                      name="Complete"
                      value={quest.completed}
                      onToggle={() => {
                        quest.completed = !quest.completed;
                        setPing(!ping);
                        onChange();
                      }} 
                    />
                  </FlexBox>
                  <FlexBox xs_expand={0}>
                    { quest.completed && 
                      <ToggleButtonBox
                        name="Rewards Granted"
                        value={quest.rewards_granted}
                        onToggle={() => {
                          quest.rewards_granted = !quest.rewards_granted;
                          setPing(!ping);
                          onChange();
                        }} 
                      />
                    }
                  </FlexBox>
                  <FlexBox xs_expand={0}>
                    <EyeButton
                      fab
                      closed={quest.hidden}
                      onClick={() => {
                        quest.hidden = !quest.hidden;
                        setPing(!ping);
                        onChange();
                      }} 
                    />
                  </FlexBox>
                </FlexBox>
              }
            </FlexBox>
            <FlexBox direction="row">
              <FlexBox xs_expand={1} direction="column">
                <FlexBox direction="row">
                  <FlexBox>
                    { enabled ? 
                      <div style={{ backgroundColor: "black" }}>
                        <StringBox 
                          name="Posted By"
                          value={quest.posted_by}
                          onBlur={ async (changed: string) => {
                            quest.posted_by = changed;
                            onChange();
                          }}
                        />
                      </div>
                    : 
                      <span className="small_label gray">Posted By: {quest.posted_by}</span>
                    }
                  </FlexBox>
                </FlexBox>
                { renderPosted() }
              </FlexBox>
              <FlexBox xs_expand={0}>
                <button
                  style={ enabled ? {} : claimBtnStyle}
                  aria-label={quest.claimed_by === "The Party" ? "Mark quest unclaimed" : "Claim Quest"}
                  title={quest.claimed_by === "The Party" ? "Mark quest unclaimed" : "Claim Quest"}
                  onClick={() => {
                    quest.claimed_by = quest.claimed_by === "The Party" ? "" : "The Party";
                    setPing(!ping);
                    onChange();
                  }}
                  onMouseEnter={() => setClaimHovered(true)}
                  onMouseLeave={() => setClaimHovered(false)}
                >
                  {quest.claimed_by === "The Party" ? "✓" : ""}
                </button>
              </FlexBox>
            </FlexBox>
            <FlexBox direction="row" style={ enabled ? {} : styles.questBody}>
              <FlexBox>
                { enabled ? 
                  <div style={{ backgroundColor: "black" }}>
                    <StringBox 
                      name="Body"
                      value={quest.body}
                      multiline
                      onBlur={ async (changed: string) => {
                        quest.body = changed;
                        onChange();
                      }}
                    />
                  </div>
                : 
                  <DisplayStringWithReplacements display_string={quest.body} /> 
                }
              </FlexBox>
            </FlexBox>
            <FlexBox direction="row" style={ enabled ? {} : styles.questMeta}>
              <FlexBox style={ enabled ? {} : styles.reward}>
                { enabled ? 
                  <div style={{ backgroundColor: "black" }}>
                    <QuestRewardsEdit
                      campaign={campaign}
                      reward={quest.reward}
                      onChange={onChange}
                    />
                  </div>
                : <>
                    Reward: 
                    <span style={styles.rewardBold}>
                      <DisplayStringWithReplacements display_string={quest.reward.text} /> 
                      {/* { renderRewardDetails() } */}
                    </span>
                    { renderGrantRewards() }
                  </> 
                }
              </FlexBox>
            </FlexBox>
            <FlexBox>
              { enabled &&
                <div style={{ backgroundColor: "black" }}>
                  <StringBox 
                    name="Claimed By"
                    value={quest.claimed_by}
                    multiline
                    onBlur={ async (changed: string) => {
                      quest.claimed_by = changed;
                      onChange();
                    }}
                  />
                </div>
              }
            </FlexBox>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={ enabled ? {} : questStyle }
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}>
        { theCard() }
      </div>
      { renderExtras() }
    </>
  );
}